<template>
  <HrPageLayout
    title="Remote Attendance Approvals"
    subtitle="Review and approve remote login requests"
    icon="🔄"
    :breadcrumb-items="[
      { label: 'Manager Dashboard', route: '/manager-dashboard' },
      { label: 'Remote Attendance' }
    ]"
  >
    <template #header-actions>
      <select v-model="filters.status" class="hr-select" style="width: 160px" @change="fetchRequests">
        <option value="Pending">Pending</option>
        <option value="Approved">Approved</option>
        <option value="Rejected">Rejected</option>
        <option value="All">All</option>
      </select>
      <input type="date" v-model="filters.dateFrom" class="hr-input" style="width: 150px" @change="fetchRequests">
      <input type="date" v-model="filters.dateTo" class="hr-input" style="width: 150px" @change="fetchRequests">
      <button class="hr-btn hr-btn-secondary" @click="resetFilters">
        <i class="bx bx-refresh"></i> Reset
      </button>
    </template>

    <div class="remote-attendance-content">
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">
            <i class="bx bx-time-five"></i> Pending Remote Logins
          </h5>
        </div>
        <div class="hr-chart-body p-0">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading remote attendance requests...</p>
          </div>
          <div v-else-if="requests.length === 0" class="hr-empty-state">
            <i class="bx bx-clipboard"></i>
            <h5>No requests found</h5>
            <p>There are no remote logins awaiting your action.</p>
          </div>
          <template v-else>
            <div class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Date & Time</th>
                    <th>Location</th>
                    <th>Action</th>
                    <th>Status</th>
                    <th style="text-align: right;">Operations</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="request in requests" :key="request.id">
                    <td>
                      <div class="hr-flex hr-flex-center hr-gap-2">
                        <div class="avatar-initials">{{ getInitials(request.employeeName) }}</div>
                        <div>
                          <div class="emp-name">{{ request.employeeName }}</div>
                          <small class="emp-meta">{{ request.empNo }} · {{ request.departmentName || '—' }}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{{ formatDate(request.accd) }}</strong>
                        <div class="hr-text-muted">{{ formatTime(request.acct) }}</div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <span class="hr-badge" :class="getLocationBadge(request.location_type)">
                          <i class="bx bx-map hr-me-2"></i>{{ request.location_type }}
                        </span>
                        <small class="d-block hr-text-muted">{{ request.location_address || '—' }}</small>
                      </div>
                    </td>
                    <td>
                      <span class="hr-badge hr-badge-muted">
                        <i :class="request.action === 'IN' ? 'bx bx-log-in' : 'bx bx-log-out'" class="hr-me-2"></i>
                        {{ request.action === 'IN' ? 'Clock In' : 'Clock Out' }}
                      </span>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getStatusBadge(request.status)">{{ request.status }}</span>
                      <div v-if="request.status !== 'Pending'" class="hr-text-muted" style="font-size: 12px; margin-top: 4px;">
                        {{ formatDateTime(request.approved_at) }}
                      </div>
                    </td>
                    <td style="text-align: right;">
                      <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content: flex-end;">
                        <button class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" @click="openDetails(request)" title="View Details">
                          <i class="bx bx-show"></i>
                        </button>
                        <button v-if="request.status === 'Pending'" class="hr-btn hr-btn-success hr-btn-sm hr-btn-icon" @click="openDecisionModal(request, 'APPROVE')" title="Approve">
                          <i class="bx bx-check"></i>
                        </button>
                        <button v-if="request.status === 'Pending'" class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon" @click="openDecisionModal(request, 'REJECT')" title="Reject">
                          <i class="bx bx-x"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="pagination.total > paginationPerPage" class="hr-pagination">
              <div class="hr-pagination-info">Page {{ pagination.page }} of {{ pagination.pages }}</div>
              <nav class="hr-pagination-nav">
                <button class="hr-page-btn" :disabled="pagination.page <= 1" @click="changePage(pagination.page - 1)">
                  <i class="bx bx-chevron-left"></i>
                </button>
                <button class="hr-page-btn" :disabled="pagination.page >= pagination.pages" @click="changePage(pagination.page + 1)">
                  <i class="bx bx-chevron-right"></i>
                </button>
              </nav>
            </div>
          </template>
        </div>
      </div>

      <!-- Request Details Modal -->
      <ModalWrapper ref="detailsModal" title="Remote Attendance Details" size="large">
        <template #body>
          <div v-if="selectedRequest" class="detail-grid">
            <div class="detail-item">
              <h6>Employee</h6>
              <p class="mb-0">{{ selectedRequest.employeeName }}</p>
              <small class="hr-text-muted">{{ selectedRequest.empNo }}</small>
            </div>
            <div class="detail-item">
              <h6>Date & Time</h6>
              <p class="mb-0">{{ formatDate(selectedRequest.accd) }}</p>
              <small class="hr-text-muted">{{ formatTime(selectedRequest.acct) }}</small>
            </div>
            <div class="detail-item">
              <h6>Action</h6>
              <span class="hr-badge hr-badge-muted">{{ selectedRequest.action === 'IN' ? 'Clock In' : 'Clock Out' }}</span>
            </div>
            <div class="detail-item">
              <h6>Status</h6>
              <span class="hr-badge" :class="getStatusBadge(selectedRequest.status)">{{ selectedRequest.status }}</span>
            </div>
            <div class="detail-item span-full">
              <h6>Location</h6>
              <p class="mb-0">{{ selectedRequest.location_address || '—' }}</p>
              <small class="hr-text-muted">Coordinates: {{ selectedRequest.latitude || '—' }}, {{ selectedRequest.longitude || '—' }}</small>
            </div>
            <div v-if="selectedRequest.notes" class="detail-item span-full">
              <h6>Notes</h6>
              <p class="mb-0">{{ selectedRequest.notes }}</p>
            </div>
            <div v-if="selectedRequest.approval_comment" class="detail-item span-full">
              <h6>Approval Comment</h6>
              <p class="mb-0">{{ selectedRequest.approval_comment }}</p>
            </div>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-secondary" @click="$refs.detailsModal.close()">Close</button>
        </template>
      </ModalWrapper>

      <!-- Decision Modal -->
      <ModalWrapper ref="decisionModal" :title="decision === 'APPROVE' ? 'Approve Remote Attendance' : 'Reject Remote Attendance'" size="medium">
        <template #body>
          <div v-if="selectedRequest">
            <p><strong>{{ selectedRequest.employeeName }}</strong><br>
            <small class="hr-text-muted">{{ formatDate(selectedRequest.accd) }} @ {{ formatTime(selectedRequest.acct) }}</small></p>
            <div class="hr-mb-3">
              <label class="hr-form-label">Comment (optional)</label>
              <textarea v-model="decisionComment" rows="3" class="hr-textarea" placeholder="Add an optional comment"></textarea>
            </div>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-secondary" @click="$refs.decisionModal.close()">Cancel</button>
          <button class="hr-btn" :class="decision === 'APPROVE' ? 'hr-btn-success' : 'hr-btn-danger'" :disabled="decisionProcessing" @click="submitDecision">
            <span v-if="decisionProcessing" class="hr-spinner-inline"></span>
            {{ decision === 'APPROVE' ? 'Approve' : 'Reject' }}
          </button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute } from "@/api";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "RemoteAttendanceApprovals",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      requests: [],
      filters: { status: "Pending", dateFrom: "", dateTo: "" },
      pagination: { total: 0, page: 1, pages: 1 },
      paginationPerPage: 50,
      selectedRequest: null,
      decision: null,
      decisionComment: "",
      decisionProcessing: false
    };
  },
  mounted() {
    this.fetchRequests();
  },
  methods: {
    async fetchRequests(page = 1) {
      this.loading = true;
      this.pagination.page = page;
      try {
        const data = new FormData();
        data.append("function", "getRemoteAttendanceRequests");
        data.append("status", this.filters.status || "Pending");
        if (this.filters.dateFrom) data.append("dateFrom", this.filters.dateFrom);
        if (this.filters.dateTo) data.append("dateTo", this.filters.dateTo);
        data.append("limit", this.paginationPerPage);
        data.append("page", this.pagination.page);

        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.requests = res.data.data.requests || [];
          this.pagination = res.data.data.pagination || this.pagination;
        } else {
          this.requests = [];
          throw new Error(res?.data?.message || "Failed to fetch requests");
        }
      } catch (error) {
        this.requests = [];
        this.$toast?.error("Error loading requests: " + error.message) || console.error("Error:", error);
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters.status = "Pending";
      this.filters.dateFrom = "";
      this.filters.dateTo = "";
      this.fetchRequests(1);
    },
    changePage(newPage) {
      if (newPage < 1 || newPage > this.pagination.pages) return;
      this.fetchRequests(newPage);
    },
    openDetails(request) {
      this.selectedRequest = request;
      this.$nextTick(() => this.$refs.detailsModal?.open());
    },
    openDecisionModal(request, decision) {
      this.selectedRequest = request;
      this.decision = decision;
      this.decisionComment = "";
      this.$nextTick(() => this.$refs.decisionModal?.open());
    },
    async submitDecision() {
      if (!this.selectedRequest || !this.decision) return;
      this.decisionProcessing = true;
      try {
        const data = new FormData();
        data.append("function", "updateRemoteAttendanceRequest");
        data.append("requestId", this.selectedRequest.id);
        data.append("decision", this.decision);
        if (this.decisionComment) data.append("comment", this.decisionComment);

        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Updated successfully") || alert(res.data.message || "Updated successfully");
          this.$refs.decisionModal?.close();
          this.fetchRequests(this.pagination.page);
        } else {
          throw new Error(res?.data?.message || "Failed to update request");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.decisionProcessing = false;
      }
    },
    getInitials(name) {
      if (!name) return "??";
      const parts = name.split(" ");
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
    },
    formatDate(date) {
      if (!date) return "—";
      return new Date(date).toLocaleDateString();
    },
    formatTime(time) {
      if (!time) return "—";
      return new Date(`1970-01-01T${time}`).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    formatDateTime(datetime) {
      if (!datetime) return "";
      return new Date(datetime).toLocaleString();
    },
    getLocationBadge(type) {
      if (type === "Office") return "hr-badge-success";
      if (type === "Outside") return "hr-badge-warning";
      return "hr-badge-info";
    },
    getStatusBadge(status) {
      if (!status) return "hr-badge-muted";
      const lowered = status.toLowerCase();
      if (lowered === "pending") return "hr-badge-warning";
      if (lowered === "approved") return "hr-badge-success";
      if (lowered === "rejected") return "hr-badge-danger";
      return "hr-badge-muted";
    }
  }
};
</script>

<style scoped>
.p-0 { padding: 0; }
.d-block { display: block; }

.avatar-initials {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  background: #dbeafe;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
}

.emp-name { font-weight: 600; font-size: 14px; }
.emp-meta { font-size: 12px; color: #64748b; display: block; }

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.detail-item { }
.detail-item.span-full { grid-column: 1 / -1; }
.detail-item h6 { font-size: 12px; color: #64748b; margin: 0 0 8px 0; text-transform: uppercase; }
.detail-item p, .detail-item small { margin: 0; }

.hr-spinner-inline {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}

@keyframes spin { to { transform: rotate(360deg); } }
</style>
